import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { url } from '../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import Telefone from '../utils/Telefone';
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Phone,
  Lock,
  Security,
  Business,
  Badge,
  CloudUpload,
  Image
} from '@mui/icons-material';
import { toast } from 'sonner';
import { useTokenRefresh } from '../../hooks/useTokenRefresh';

const theme = createTheme();

// Styled components para elementos modernos
const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '25px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 32px',
  fontSize: '1.1rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '15px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,1)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255,255,255,1)',
      transform: 'translateY(-1px)',
    },
  },
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
  },
}));

export default function Perfil({ usePageTitle }) {
  usePageTitle('PixTick | Perfil');
  const navigate = useNavigate();
  const { logout, updateUserData } = useAuth();
  const { makeAuthenticatedRequest } = useTokenRefresh();
  const [error, setError] = React.useState(null);
  const [message, setMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [senhaValida, setSenhaValida] = React.useState(false);
  const [qtdCaracteresValida, setQtdCaracteresValida] = React.useState(false);
  const [temMaiuscula, setTemMaiuscula] = React.useState(false);
  const [temMinuscula, setTemMinuscula] = React.useState(false);
  const [temNumero, setTemNumero] = React.useState(false);
  const [temCaracterEspecial, setTemCaracterEspecial] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(0);
  const patternUPPER = new RegExp("^(?=.*[A-Z])");
  const patternLOWER = new RegExp("^(?=.*[a-z])");
  const patternNUMBER = new RegExp("^(?=.*\\d)");
  const patternESPECIAL = new RegExp("^(?=.*[-+_!@#$%^&*.,?]).+$");
  const [openDev, setOpenDev] = React.useState(false);
  const [user, setUser] = React.useState({
    fullname: '',
    email: '',
    password: ''
  });
  const [newEmail, setNewEmail] = React.useState('');
  const [senhas, setSenhas] = React.useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [state, setState] = React.useState({
    ddi: '+55',
    phone: '',
    error: ''
  });
  const [uploadingLogo, setUploadingLogo] = React.useState(false);

  React.useEffect(() => {
    async function fetchData() {
      try {
        const token = await localStorage.getItem('token');
        const response = await axios.get(url + `/api/v1/user/perfil`, {
          headers: {
            Authorization: 'Bearer ' + token
          }
        });
        const { data } = response;
        setUser(data.user);
        setNewEmail(data.user.email); // Only update once data is fetched
        const phone = formatPhoneNumber('+' + data.user.numero);
        setState({ ...state, phone: phone.phone, ddi: phone.ddi });
        // Dados carregados com sucesso
      } catch (e) {
        console.error('Erro ao recuperar dados:', e);

        if (e.response?.status === 401) {
          toast.error("Sessão expirada", {
            description: "Faça login novamente para continuar",
            duration: 4000,
          });
        } else {
          toast.error("Erro ao carregar perfil", {
            description: "Não foi possível carregar os dados do perfil",
            duration: 4000,
          });
        }

        logout();
        navigate('/signin');
      }
    }
    fetchData();
  }, [navigate, logout]); // Removed `setState, state` to avoid unnecessary re-renders

  const handleAlterarSenha = async (event) => {
    event.preventDefault();
    try {
      const token = await localStorage.getItem('token');

      // Verificações de validação antes de tentar alterar a senha
      if (!senhaValida) {
        setMessage("A senha não atende aos critérios");
        setOpen(true);
        return;
      }

      if (senhas.newPassword !== senhas.confirmPassword) {
        setMessage("Senhas não conferem");
        setOpen(true);
        return;
      }

      const body = {
        user: {
          email: user.email,
          password: user.password,
        },
        newPassword: senhas.newPassword
      };

      // Chamada para a API
      const updateResponse = await axios.post(url + '/api/v1/user/atualizaSenha', body, {
        headers: {
          'Authorization': 'Bearer ' + token
        }
      });

      // Verificação da resposta corrigida
      if (updateResponse.data.status === "Senha atualizada com sucesso") {
        toast.success("Senha alterada com sucesso!", {
          description: "Sua senha foi atualizada com segurança.",
          duration: 3000,
        });
        navigate('/perfil');
      } else {
        toast.error("Erro ao alterar senha", {
          description: updateResponse.data.message || "Mensagem não disponível",
          duration: 4000,
        });
      }

    } catch (error) {
      console.log('Erro:', error.response ? error.response.data : error);

      if (error.response?.status === 401) {
        toast.error("Sessão expirada", {
          description: "Faça login novamente para continuar",
          duration: 4000,
        });
        logout();
        navigate('/signin');
      } else {
        const errorMessage = error.response?.data?.message || "Senha atual incorreta";
        toast.error("Erro ao alterar senha", {
          description: errorMessage,
          duration: 4000,
        });
      }
    }
  };

  const handleIsValidPassword = (password) => {
    setQtdCaracteresValida(false);
    setTemMaiuscula(false);
    setTemMinuscula(false);
    setTemNumero(false);
    setTemCaracterEspecial(false);
    setSenhaValida(false);
    if (password.length >= 8 && password.length <= 16) setQtdCaracteresValida(true);
    if (patternUPPER.test(password)) setTemMaiuscula(true);
    if (patternLOWER.test(password)) setTemMinuscula(true);
    if (patternNUMBER.test(password)) setTemNumero(true);
    if (patternESPECIAL.test(password)) setTemCaracterEspecial(true);
    if (password.length >= 8 && password.length <= 16 && patternUPPER.test(password) && patternLOWER.test(password) && patternNUMBER.test(password) && patternESPECIAL.test(password)) {
      setSenhaValida(true);
      return true;
    }
    return false;
  }

  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Verificar se é uma imagem
    if (!file.type.startsWith('image/')) {
      toast.error('Erro', {
        description: 'Por favor, selecione apenas arquivos de imagem',
        duration: 4000,
      });
      return;
    }

    // Verificar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Erro', {
        description: 'Arquivo muito grande. Máximo 5MB',
        duration: 4000,
      });
      return;
    }

    setUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${url}/api/v1/user/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.data.message) {
        // Atualizar o estado do usuário com o novo logo
        setUser(prev => ({
          ...prev,
          empresaLogo: response.data.logo
        }));

        // Atualizar também o contexto de autenticação
        updateUserData({
          empresaLogo: response.data.logo
        });

        toast.success('Logo atualizado!', {
          description: 'O logo da empresa foi atualizado com sucesso',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error);
      toast.error('Erro ao fazer upload', {
        description: error.response?.data?.error || 'Erro desconhecido',
        duration: 4000,
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleAlterarDados = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error("Token não encontrado!", {
          description: "Faça login novamente",
          duration: 4000,
        });
        logout();
        navigate('/signin');
        return;
      }

      const body = {
        user: {
          email: user.email,
          phone: (state.ddi + state.phone).replace(/[()\s+-]/g, "")
        },
        newEmail: newEmail,
      };

      const response = await axios.post(url + '/api/v1/user/atualizaDados', body, {
        headers: {
          'Authorization': 'Bearer ' + token
        }
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      toast.success("Dados alterados com sucesso!", {
        description: "Suas informações foram atualizadas.",
        duration: 3000,
      });

      navigate('/perfil');
    } catch (error) {
      console.error('Erro ao alterar dados:', error);

      if (error.response?.status === 401) {
        toast.error("Sessão expirada!", {
          description: "Faça login novamente para continuar",
          duration: 5000,
        });

        // Limpar dados de autenticação
        localStorage.removeItem('token');
        localStorage.removeItem('fullname');
        localStorage.removeItem('role');
        localStorage.removeItem('email');

        setTimeout(() => {
          logout();
          navigate('/signin');
        }, 2000);
      } else {
        const errorMessage = error.response?.data?.message || "Erro ao alterar dados";
        toast.error("Erro ao alterar dados", {
          description: errorMessage,
          duration: 5000,
        });
      }
    }
  };

  function formatPhoneNumber(phoneNumber) {
    // Retorna o valor de entrada se for null ou undefined
    if (!phoneNumber) return phoneNumber;

    // Define as máscaras para diferentes códigos de país
    const masks = {
      '+55': { name: 'Brasil', mask: '(xx) xxxxx-xxxx' },
      '+54': { name: 'Argentina', mask: '(x) xxxx xx-xxxx' },
      '+595': { name: 'Paraguai', mask: '(xxx) xxx-xxx' },
      '+1': { name: 'EUA', mask: '(xxx) xxx-xxxx' }
    };

    // Encontra o código de país (ddi) que corresponde ao início do número de telefone
    let ddi = Object.keys(masks).find(ddi => phoneNumber.startsWith(ddi));
    if (!ddi) return phoneNumber;

    // Remove o código de país do número de telefone
    phoneNumber = phoneNumber.replace(ddi, '');

    // Obtém a máscara correspondente ao código de país encontrado
    const mask = masks[ddi].mask;

    // Extrai apenas os dígitos do número de telefone
    const numericPhoneNumber = phoneNumber.replace(/\D/g, '');

    let formattedPhoneNumber = '';
    let maskIndex = 0;

    // Aplica a máscara ao número de telefone numérico
    for (let i = 0; i < mask.length && maskIndex < numericPhoneNumber.length; i++) {
      if (mask[i] === 'x') {
        formattedPhoneNumber += numericPhoneNumber[maskIndex];
        maskIndex++;
      } else {
        formattedPhoneNumber += mask[i];
      }
    }

    return { ddi, phone: formattedPhoneNumber };
  }

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangeNewPassword = (event) => {
    const { value } = event.target;
    setSenhas({ ...senhas, newPassword: value });
    handleIsValidPassword(value);
  }

  const handleNewEmail = (event) => {
    setNewEmail(event.target.value);
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 0.5, sm: 2 },
        // Ajustar para mobile - mesmo padrão do signup
        pt: { xs: 'calc(64px + max(env(safe-area-inset-top, 0px), 10px))', sm: 0 },
        pb: { xs: 2, sm: 0 }
      }}>
        <Container maxWidth="md" sx={{
          width: '100%',
          padding: { xs: 0, sm: 2 },
          maxWidth: { xs: '100%', md: 'md' }
        }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 3
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleChange}
              aria-label="profile tabs"
              sx={{
                '& .MuiTab-root': {
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&.Mui-selected': {
                    color: '#FF6700',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#FF6700',
                  height: 3,
                  borderRadius: '2px 2px 0 0',
                },
              }}
            >
              <Tab
                label="Dados Pessoais"
                icon={<Person />}
                iconPosition="start"
              />
              <Tab
                label="Segurança"
                icon={<Security />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <ModernCard
              sx={{
                width: '100%',
                margin: '0 auto',
                backgroundColor: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    textAlign: 'center',
                    mb: 3,
                    fontWeight: 600,
                    color: '#1976D2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}
                >
                  <Person color="primary" />
                  Dados Pessoais
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{
                        p: 2,
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant='h6' sx={{ fontWeight: 600, color: '#1976D2' }}>
                          {user.fullname}
                        </Typography>
                        <Typography variant='body2' color="text.secondary">
                          Nome Completo
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{
                        p: 2,
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant='h6' sx={{ fontWeight: 600, color: '#1976D2' }}>
                          {user.idpdv ? user.idpdv : '-'}
                        </Typography>
                        <Typography variant='body2' color="text.secondary">
                          IDPDV
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 2,
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant='h6' sx={{ fontWeight: 600, color: '#1976D2' }}>
                          {user.empresa ? user.empresa : '-'}
                        </Typography>
                        <Typography variant='body2' color="text.secondary">
                          Empresa
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Seção de Logo da Empresa (apenas para ADMIN) */}
                {user.role === 'ADMIN' && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: '#1976D2',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Image color="primary" />
                      Logo da Empresa
                    </Typography>
                    
                    <Box sx={{
                      p: 3,
                      border: '2px dashed #1976D2',
                      borderRadius: 2,
                      textAlign: 'center',
                      backgroundColor: 'rgba(25, 118, 210, 0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        borderColor: '#1565C0'
                      }
                    }}>
                      {user.empresaLogo ? (
                        <Box sx={{ mb: 2 }}>
                          <img
                            src={user.empresaLogo}
                            alt="Logo da empresa"
                            style={{
                              width: 100,
                              height: 100,
                              objectFit: 'cover',
                              borderRadius: '50%',
                              border: '3px solid #1976D2'
                            }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ mb: 2 }}>
                          <Image sx={{ fontSize: 60, color: '#1976D2', opacity: 0.5 }} />
                        </Box>
                      )}
                      
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                        id="logo-upload"
                        disabled={uploadingLogo}
                      />
                      
                      <label htmlFor="logo-upload">
                        <Button
                          component="span"
                          variant="contained"
                          startIcon={<CloudUpload />}
                          disabled={uploadingLogo}
                          sx={{
                            backgroundColor: '#1976D2',
                            '&:hover': {
                              backgroundColor: '#1565C0'
                            }
                          }}
                        >
                          {uploadingLogo ? 'Enviando...' : user.empresaLogo ? 'Alterar Logo' : 'Carregar Logo'}
                        </Button>
                      </label>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Formatos aceitos: JPG, PNG, GIF (máximo 5MB)
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ModernTextField
                      fullWidth
                      id="email"
                      label="Email"
                      name="email"
                      autoComplete="email"
                      value={user.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Telefone state={state} setState={setState} />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <ModernButton
                  fullWidth
                  variant="contained"
                  onClick={handleAlterarDados}
                  sx={{
                    backgroundColor: '#1976D2',
                    '&:hover': {
                      backgroundColor: '#1565C0',
                    }
                  }}
                >
                  Salvar Alterações
                </ModernButton>
              </CardContent>
            </ModernCard>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <ModernCard
              sx={{
                width: '100%',
                margin: '0 auto',
                backgroundColor: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    textAlign: 'center',
                    mb: 3,
                    fontWeight: 600,
                    color: '#1976D2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}
                >
                  <Security color="primary" />
                  Segurança
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ModernTextField
                      required
                      fullWidth
                      name="currentPassword"
                      label="Senha Atual"
                      type={showCurrentPassword ? 'text' : 'password'}
                      id="currentPassword"
                      value={user.password}
                      onChange={(e) => setUser({ ...user, password: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={toggleCurrentPasswordVisibility}
                              edge="end"
                            >
                              {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ModernTextField
                      required
                      fullWidth
                      name="newPassword"
                      label="Nova Senha"
                      type={showNewPassword ? 'text' : 'password'}
                      id="newPassword"
                      value={senhas.newPassword}
                      onChange={handleChangeNewPassword}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={toggleNewPasswordVisibility}
                              edge="end"
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: senhaValida ? 'green' : 'red' }}>
                        A senha deve ter pelo menos:
                      </Typography>
                      <Typography variant="body2" sx={{ color: qtdCaracteresValida ? 'green' : 'red', display: 'flex', alignItems: 'center', gap: 1 }}>
                        • 8 a 16 caracteres
                      </Typography>
                      <Typography variant="body2" sx={{ color: temMaiuscula ? 'green' : 'red', display: 'flex', alignItems: 'center', gap: 1 }}>
                        • Pelo menos 1 maiúscula
                      </Typography>
                      <Typography variant="body2" sx={{ color: temMinuscula ? 'green' : 'red', display: 'flex', alignItems: 'center', gap: 1 }}>
                        • Pelo menos 1 minúscula
                      </Typography>
                      <Typography variant="body2" sx={{ color: temNumero ? 'green' : 'red', display: 'flex', alignItems: 'center', gap: 1 }}>
                        • Pelo menos 1 número
                      </Typography>
                      <Typography variant="body2" sx={{ color: temCaracterEspecial ? 'green' : 'red', display: 'flex', alignItems: 'center', gap: 1 }}>
                        • Pelo menos 1 caractere especial
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <ModernTextField
                      required
                      fullWidth
                      name="confirmPassword"
                      label="Confirme a Senha"
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      value={senhas.confirmPassword}
                      onChange={(e) => setSenhas({ ...senhas, confirmPassword: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={toggleConfirmPasswordVisibility}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <ModernButton
                  fullWidth
                  variant="contained"
                  onClick={handleAlterarSenha}
                  sx={{
                    backgroundColor: '#1976D2',
                    '&:hover': {
                      backgroundColor: '#1565C0',
                    }
                  }}
                >
                  Alterar Senha
                </ModernButton>
              </CardContent>
            </ModernCard>
          </TabPanel>
          <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setOpen(false)} severity={message.includes('sucesso') ? 'success' : 'error'} sx={{ width: '100%' }}>
              {message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

